import { forwardRef, useEffect, useState, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";

const InputOTP = forwardRef(
  (
    {
      length = 6,
      value = "",
      onChange,
      onComplete,
      disabled = false,
      autoFocus = true,
      isNumericOnly = true,
      placeholder = "",
      containerClassName = "",
      inputClassName = "",
      error = false,
      allowPaste = true,
      secure = false,
      autoSubmit = false,
      autoSubmitDelay = 500,
      className,
      ...props
    },
    ref,
  ) => {
    const [otp, setOtp] = useState(value.split("").slice(0, length));
    const inputRefs = useRef([]);
    const autoSubmitTimeoutRef = useRef(null);

    // Initialize refs
    useEffect(() => {
      inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    // Auto-focus first input
    useEffect(() => {
      if (autoFocus && !disabled && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, [autoFocus, disabled]);

    // Sync with external value changes
    useEffect(() => {
      const newOtp = value.split("").slice(0, length);
      setOtp(newOtp);
    }, [value, length]);

    // Focus specific input
    const focusInput = useCallback((index) => {
      if (inputRefs.current[index]) {
        inputRefs.current[index].focus();
        inputRefs.current[index].select();
      }
    }, []);

    // Handle input change
    const handleChange = useCallback(
      (index, e) => {
        if (disabled) return;

        const inputValue = e.target.value;
        const lastChar = inputValue.slice(-1);

        // Validate numeric only if required
        if (isNumericOnly && lastChar && !/^\d$/.test(lastChar)) {
          return;
        }

        const newOtp = [...otp];
        newOtp[index] = lastChar;
        setOtp(newOtp);

        // Call onChange callback
        const otpString = newOtp.join("");
        if (onChange) {
          onChange(otpString);
        }

        // Move to next input if value entered
        if (lastChar && index < length - 1) {
          focusInput(index + 1);
        }

        // Check if OTP is complete
        if (newOtp.every((digit) => digit !== "") && newOtp.length === length) {
          if (onComplete) {
            if (autoSubmit) {
              if (autoSubmitTimeoutRef.current) {
                clearTimeout(autoSubmitTimeoutRef.current);
              }
              autoSubmitTimeoutRef.current = setTimeout(() => {
                onComplete(otpString);
              }, autoSubmitDelay);
            } else {
              onComplete(otpString);
            }
          }
        }
      },
      [
        otp,
        length,
        onChange,
        onComplete,
        disabled,
        isNumericOnly,
        autoSubmit,
        autoSubmitDelay,
        focusInput,
      ],
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (index, e) => {
        if (disabled) return;

        const { key } = e;

        if (key === "Backspace") {
          e.preventDefault();
          const newOtp = [...otp];

          if (otp[index]) {
            newOtp[index] = "";
            setOtp(newOtp);
            if (onChange) onChange(newOtp.join(""));
          } else if (index > 0) {
            newOtp[index - 1] = "";
            setOtp(newOtp);
            if (onChange) onChange(newOtp.join(""));
            focusInput(index - 1);
          }
        } else if (key === "Delete") {
          e.preventDefault();
          const newOtp = [...otp];
          newOtp[index] = "";
          setOtp(newOtp);
          if (onChange) onChange(newOtp.join(""));
        } else if (key === "ArrowLeft" && index > 0) {
          e.preventDefault();
          focusInput(index - 1);
        } else if (key === "ArrowRight" && index < length - 1) {
          e.preventDefault();
          focusInput(index + 1);
        } else if (key === "Home") {
          e.preventDefault();
          focusInput(0);
        } else if (key === "End") {
          e.preventDefault();
          focusInput(length - 1);
        }
      },
      [otp, length, onChange, disabled, focusInput],
    );

    // Handle paste
    const handlePaste = useCallback(
      (e) => {
        if (!allowPaste || disabled) {
          e.preventDefault();
          return;
        }

        e.preventDefault();
        const pastedData = e.clipboardData
          .getData("text/plain")
          .slice(0, length);

        if (isNumericOnly && !/^\d+$/.test(pastedData)) {
          return;
        }

        const newOtp = pastedData.split("").slice(0, length);
        while (newOtp.length < length) {
          newOtp.push("");
        }

        setOtp(newOtp);
        if (onChange) onChange(newOtp.join(""));

        const lastFilledIndex = pastedData.length - 1;
        const nextIndex = Math.min(lastFilledIndex + 1, length - 1);
        focusInput(nextIndex);

        if (pastedData.length === length && onComplete) {
          if (autoSubmit) {
            if (autoSubmitTimeoutRef.current) {
              clearTimeout(autoSubmitTimeoutRef.current);
            }
            autoSubmitTimeoutRef.current = setTimeout(() => {
              onComplete(pastedData);
            }, autoSubmitDelay);
          } else {
            onComplete(pastedData);
          }
        }
      },
      [
        length,
        onChange,
        onComplete,
        allowPaste,
        disabled,
        isNumericOnly,
        autoSubmit,
        autoSubmitDelay,
        focusInput,
      ],
    );

    // Handle focus
    const handleFocus = useCallback((index) => {
      if (inputRefs.current[index]) {
        inputRefs.current[index].select();
      }
    }, []);

    // Handle blur - placeholder for potential future use
    const handleBlur = useCallback(() => {
      // No-op: available for future blur handling
    }, []);

    // Cleanup
    useEffect(
      () => () => {
        if (autoSubmitTimeoutRef.current) {
          clearTimeout(autoSubmitTimeoutRef.current);
        }
      },
      [],
    );

    const setInputRef = (index) => (el) => {
      inputRefs.current[index] = el;
    };

    return (
      <div className={cn("w-full", className)} ref={ref} {...props}>
        <div className={cn("flex gap-2 justify-center", containerClassName)}>
          {Array.from({ length }, (_, index) => (
            <input
              key={index}
              ref={setInputRef(index)}
              type={secure ? "password" : "text"}
              inputMode={isNumericOnly ? "numeric" : "text"}
              maxLength={1}
              value={otp[index] || ""}
              onChange={(e) => handleChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => handleFocus(index)}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder={placeholder}
              className={cn(
                "relative flex h-9 w-9 items-center justify-center bg-background",
                "border border-input text-center text-base md:text-sm",
                "rounded-md transition-all",
                "file:border-0 file:bg-transparent file:text-sm file:font-medium",
                "placeholder:text-muted-foreground",
                "disabled:cursor-not-allowed disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                error && "border-destructive focus-visible:ring-destructive",
                !disabled && "hover:border-primary/50",
                inputClassName,
              )}
              aria-label={`OTP digit ${index + 1}`}
              autoComplete="off"
            />
          ))}
        </div>
      </div>
    );
  },
);
InputOTP.displayName = "InputOTP";

export { InputOTP };
